"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { createClient } from "@/libs/supabase/client";
import { User } from "@supabase/supabase-js";
import ButtonSignin from "@/components/ButtonSignin";
import ButtonCheckout from "@/components/ButtonCheckout";
import FAQSection from "@/components/FAQSection/FAQStatic";
import V3WalkthroughVideo from "@/app/blog/_assets/components/V3WalkthroughVideo";
import config from "@/config";

export default function PricingPage() {
  const supabase = createClient();
  const [user, setUser] = useState<User | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
  }, [supabase]);

  return (
    // translate="no" (2026-09-14): this page is a client component whose
    // header/mobile-menu conditionally swaps text after async auth — Chrome
    // page translation wraps those text nodes in <font> and React's commit
    // then crashes with removeChild/insertBefore NotFoundError (react#11538).
    // Observed in prod: two client.app-error HIGHs on /pricing, 2026-09-14.
    <div className="min-h-screen bg-base-100" translate="no">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-base-100/95 backdrop-blur-sm border-b border-base-200">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <Link href="/" className="flex items-center gap-2">
              <Image 
                src="/general/MMP Color Logo.png" 
                alt="ChangeLyric" 
                width={32} 
                height={32} 
                className="rounded-full object-cover"
              />
              <span className="text-xl font-bold text-primary hidden sm:inline">
                ChangeLyric
              </span>
            </Link>
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-4">
              <Link href="/" className="btn btn-ghost btn-sm">Home</Link>
              {user ? (
                <>
                  <Link href="/dashboard" className="btn btn-primary btn-sm">
                    Go to App
                  </Link>
                  <button
                    onClick={async () => {
                      await supabase.auth.signOut();
                      window.location.href = '/';
                    }}
                    className="btn btn-ghost btn-sm"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <ButtonSignin text="Sign In" extraStyle="btn-sm" />
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden btn btn-ghost btn-sm btn-square"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-base-200 bg-base-100/95 backdrop-blur-sm">
            <div className="container mx-auto px-4 py-4">
              <div className="flex flex-col gap-2">
                <Link 
                  href="/" 
                  className="btn btn-ghost btn-sm justify-start"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <div className="pt-2 mt-2 border-t border-base-200">
                  {user ? (
                    <>
                      <Link
                        href="/dashboard"
                        className="btn btn-primary btn-sm btn-block"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Go to App
                      </Link>
                      <button
                        onClick={async () => {
                          await supabase.auth.signOut();
                          window.location.href = '/';
                        }}
                        className="btn btn-ghost btn-sm btn-block mt-2"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <ButtonSignin text="Sign In" extraStyle="btn-sm btn-block" />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Pricing Hero */}
      <section className="py-12 sm:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl sm:text-5xl font-extrabold mb-4">
              Try ChangeLyric For Free
            </h1>
            <p className="text-xl text-base-content/70 mb-4">
              Start with a 7-day free trial. Cancel anytime.
            </p>
            <div className="badge badge-primary badge-lg">
              V3 Beta Special - Save 40%
            </div>
          </div>

          {/* Trial + credit clarity. The single biggest source of support
              tickets is "I thought this was free / why was I charged." Put the
              plain-English version right where they enter their card. */}
          <div className="max-w-2xl mx-auto mb-10 p-4 bg-success/10 border border-success/20 rounded-xl text-left text-sm text-base-content/80 space-y-2">
            <p className="font-semibold text-base-content">Your card is not charged today.</p>
            <p>
              The 7-day trial is free. Your card stays on file and is only
              charged if you keep your plan past day 7. Cancel before then and
              you pay nothing.
            </p>
            <p>
              <span className="font-medium">Credits</span> power your lyric
              swaps. Starter includes 150 a month, Pro includes 1,000, and they
              reset on the 1st.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {config.stripe.plans.map((plan) => {
              const isStarter = plan.name === "Starter";

              return (
                <div
                  key={plan.priceId}
                  className={`card bg-base-100 shadow-xl overflow-hidden ${
                    isStarter ? "ring-2 ring-primary" : "ring-1 ring-base-300"
                  } flex flex-col`}
                >
                  <div className={isStarter ? "" : "h-[36px]"}>
                    {isStarter && (
                      <div className="bg-primary text-primary-content text-center py-2 text-sm font-semibold">
                        MOST POPULAR
                      </div>
                    )}
                  </div>

                  <div className="card-body p-8 flex-1 flex flex-col">
                    <div>
                      <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                      <p className="text-base-content/70 mb-6">{plan.description}</p>
                    </div>

                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold">${plan.price}</span>
                        <span className="text-base-content/70">/month</span>
                      </div>
                      {plan.priceAnchor > 0 && (
                        <div className="text-sm text-base-content/50 line-through">
                          ${plan.priceAnchor}/month
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-base-content/50 mb-4">7-day free trial included</p>

                    {/* Features */}
                    <ul className="space-y-3 mb-8 flex-1">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <svg
                            className="w-5 h-5 text-success flex-shrink-0 mt-0.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-sm">{feature.name}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    {user ? (
                      <ButtonCheckout
                        priceId={plan.priceId}
                        mode="subscription"
                      />
                    ) : (
                      <ButtonSignin
                        text="Start Free Trial"
                        extraStyle={isStarter ? "btn-primary" : "btn-outline btn-primary"}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Credit Packs Section — hidden for now */}

          {/* Trial Info */}
          <div className="text-center mt-12">
            <p className="text-base-content/70">
              All subscription plans include a 7-day free trial. Cancel anytime. No hidden fees.
            </p>
            <p className="text-sm text-base-content/50 mt-2">
              Credits reset on the 1st of each month. Usage during your free trial counts toward the current month.
            </p>
          </div>

          {/* DFY Callout */}
          <div className="max-w-2xl mx-auto mt-12 p-6 bg-secondary/10 border border-secondary/20 rounded-xl text-center">
            <p className="text-lg font-semibold mb-2">Want it done for you?</p>
            <p className="text-base-content/70 mb-4">
              Skip the tools — our team will change your lyrics for you, starting at $50 per song.
            </p>
            <Link href="/service" className="btn btn-secondary btn-sm">
              Visit Our Service Page
            </Link>
          </div>

          {/* Overdub Studio Walkthrough Video */}
          <div className="max-w-3xl mx-auto mt-16 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              See Overdub Studio in action before you start
            </h2>
            <p className="text-base-content/70 mb-2">
              A 14-minute walkthrough of how the lyric swap workflow actually runs: tagging lyrics, choosing generation strategies, comping keepers, and downloading stems and comp lanes.
            </p>
          </div>
          <V3WalkthroughVideo />
        </div>
      </section>

      {/* YouTube Tutorial Section - temporarily hidden */}
      {false && <section className="py-16 sm:py-20 bg-base-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
              How to Use ChangeLyric Effectively
            </h2>
            <p className="text-center text-base-content/70 mb-8">
              Watch this before using ChangeLyric - learn the proper workflow for best results
            </p>
            <div className="relative aspect-video rounded-xl overflow-hidden shadow-2xl bg-black mb-8">
              <video
                className="w-full h-full object-cover"
                controls
                playsInline
                poster="https://cdn.shopify.com/s/files/1/0713/6677/0998/files/opengraph-image_704cc1f2-a2a6-4d2f-893d-dfebd0d13473.png?v=1755026492"
              >
                <source src="https://cdn.shopify.com/videos/c/o/v/30fba0ed7dec4318bfe818ccae95ba19.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            
            {/* Video Chapters */}
            <div className="bg-base-200 rounded-lg p-6">
              <h3 className="text-lg font-bold mb-4">Video Chapters</h3>
              <div className="space-y-2 text-sm">
                <div className="flex gap-3">
                  <span className="text-primary font-mono">0:00</span>
                  <span>Why You Must Watch This Tutorial</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-mono">0:06</span>
                  <span>Full Song Production - 10+ Generations Needed</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-mono">0:16</span>
                  <span>Best Practice: 2-4 Sections at a Time</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-mono">0:26</span>
                  <span>Using the Listen Button for Track Preview</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-mono">0:32</span>
                  <span>Window Selection - Example</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-mono">0:40</span>
                  <span>Natural Continuation Approach</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-mono">0:48</span>
                  <span>Working with Verse 1 and Chorus 1</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-mono">0:56</span>
                  <span>Improving Generation Probability</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-mono">1:00</span>
                  <span>First Attempt Results Review</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-mono">1:06</span>
                  <span>Example: Changing Theme to Outer Space</span>
                </div>
              </div>
              <div className="mt-4 p-4 bg-base-300 rounded-lg">
                <p className="text-sm text-base-content/80">
                  This tutorial explains the essential workflow for ChangeLyric: working with 2-4 sections at a time, 
                  understanding song structure, and using the continuation approach for better results. 
                  Full songs require multiple generations (10+) that you'll edit together in your DAW.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>}

      {/* FAQ Section */}
      <section className="py-12 bg-base-200">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">
              Frequently Asked Questions
            </h2>
            
            <FAQSection />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-base-200 py-8 border-t border-base-300">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            
            <div className="flex gap-4 text-sm">
              <Link href="/privacy-policy" className="link link-hover">Privacy</Link>
              <Link href="/tos" className="link link-hover">Terms</Link>
              <a href="mailto:contact@changelyric.com" className="link link-hover">Support</a>
            </div>
            
            <p className="text-sm text-base-content/60">
              © {new Date().getFullYear()} ChangeLyric
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}